import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { AlertTriangle, Send, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Bouton + modale "Demande de modification de la décision direction"
 * Visible quand : simulation a une admin_status finale (approved/rejected/counter_proposal)
 *                 ET l'utilisateur n'est PAS admin (canMakeDecision === false)
 * Workflow :
 *   - commercial soumet → email envoyé à l'auteur de la décision
 *   - auteur clique "Accepter" dans le mail → simulation déverrouillée
 *   - bandeau vert affiché : "Modification autorisée — vous pouvez modifier"
 */
export default function DecisionModificationRequest({ simulationId, simulation, token, onUnlocked }) {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = async () => {
    if (!simulationId) return;
    try {
      const res = await fetch(`${API}/api/simulations/${simulationId}/modification-request/latest`, { headers });
      if (res.ok) setLatest(await res.json());
    } catch {}
  };

  useEffect(() => { load(); }, [simulationId, simulation?.decision_modification_unlocked]);

  const isUnlocked = !!simulation?.decision_modification_unlocked;

  // Banner: décision déjà déverrouillée (approuvée par l'auteur)
  if (isUnlocked) {
    return (
      <Card className="border-emerald-300 bg-emerald-50/60" data-testid="modif-unlocked-banner">
        <CardContent className="py-3 flex items-center justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-emerald-800">
                Modification autorisée par la Direction
              </p>
              <p className="text-xs text-emerald-700 mt-0.5">
                Tu peux maintenant modifier la décision (bouton "Modifier" dans la carte Décision Direction)
                ou refaire une nouvelle proposition.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Pending request banner
  if (latest?.status === 'pending') {
    return (
      <Card className="border-amber-300 bg-amber-50/60" data-testid="modif-pending-banner">
        <CardContent className="py-3 flex items-center justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5 animate-pulse" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-800">
                Demande de modification en attente
              </p>
              <p className="text-xs text-amber-700 mt-0.5 truncate">
                Envoyée le {new Date(latest.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {' '} · En attente de validation par {latest.decision_author_name || 'la Direction'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Rejected banner: allow re-request
  const wasRejected = latest?.status === 'rejected';

  return (
    <>
      <Card className={`border-2 ${wasRejected ? 'border-slate-300 bg-slate-50' : 'border-red-300 bg-red-50/40'} animate-in fade-in`}
        data-testid="modif-request-cta">
        <CardContent className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${wasRejected ? 'text-slate-500' : 'text-red-500 animate-pulse'}`} />
            <div className="min-w-0">
              <p className={`text-sm font-semibold ${wasRejected ? 'text-slate-700' : 'text-red-700'}`}>
                {wasRejected ? 'Demande précédente refusée' : 'Tu souhaites modifier la décision direction ?'}
              </p>
              <p className="text-xs text-slate-600 mt-0.5">
                {wasRejected
                  ? `Refusée le ${new Date(latest.responded_at).toLocaleDateString('fr-FR')}. Tu peux soumettre une nouvelle demande.`
                  : 'Soumets une demande à la Direction avec les nouvelles conditions souhaitées. Une notification email sera envoyée à l\'auteur de la décision.'}
              </p>
            </div>
          </div>
          <Button onClick={() => setShowModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white gap-1 shrink-0 shadow-md"
            data-testid="open-modif-request">
            <AlertTriangle className="w-4 h-4" />
            Demande de modification
          </Button>
        </CardContent>
      </Card>

      <RequestModal
        open={showModal}
        onClose={() => setShowModal(false)}
        simulationId={simulationId}
        simulation={simulation}
        token={token}
        onSent={() => { setShowModal(false); load(); toast.success('Demande envoyée à la Direction'); if (onUnlocked) onUnlocked(); }}
      />
    </>
  );
}

function RequestModal({ open, onClose, simulationId, simulation, token, onSent }) {
  const [reason, setReason] = useState('');
  const [proposedCa, setProposedCa] = useState('');
  const [proposedCommission, setProposedCommission] = useState('');
  const [proposedCapex, setProposedCapex] = useState('');
  const [proposedOther, setProposedOther] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      // Pre-fill with current values to make life easier
      setProposedCa(simulation?.admin_counter_proposal?.ca_previsionnel_ht || simulation?.ca_previsionnel_ht_proposed || '');
      const curCom = simulation?.admin_counter_proposal?.commission_rate || simulation?.commission_rate_proposed;
      setProposedCommission(curCom != null ? (curCom * 100).toFixed(1) : '');
      setProposedCapex('');
      setProposedOther('');
    }
  }, [open, simulation]);

  const submit = async () => {
    if (reason.trim().length < 5) {
      toast.error('Renseigne le motif (min 5 caractères)');
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        reason: reason.trim(),
        proposed_ca_ht: proposedCa ? parseFloat(proposedCa) : null,
        proposed_commission: proposedCommission ? parseFloat(proposedCommission) : null,
        proposed_capex: proposedCapex ? parseFloat(proposedCapex) : null,
        proposed_other_terms: proposedOther.trim() || null,
      };
      const res = await fetch(`${API}/api/simulations/${simulationId}/modification-request`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors de l\'envoi');
        return;
      }
      onSent();
    } catch (e) {
      toast.error('Erreur réseau');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modif-request-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5 animate-pulse" />
            Demande de modification — Décision Direction
          </DialogTitle>
          <DialogDescription>
            Précise les nouvelles conditions souhaitées et le motif. La Direction recevra
            un email avec un lien <strong className="text-emerald-700">Accepter</strong> /
            <strong className="text-red-700"> Refuser</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-sm">Motif de la demande <span className="text-red-500">*</span></Label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex : Le client a renégocié à la baisse, il faut revoir la commission de 18 % à 17,5 %…"
              className="w-full border rounded-md p-2 text-sm min-h-[100px] mt-1"
              data-testid="modif-reason"
            />
            <p className="text-[11px] text-slate-400 mt-0.5">{reason.length} / 2000 caractères</p>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Nouvelles conditions proposées</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">CA prévisionnel HT (€)</Label>
                <Input type="number" step="1000" value={proposedCa} onChange={(e) => setProposedCa(e.target.value)}
                  placeholder="Ex 350000" data-testid="modif-ca" />
              </div>
              <div>
                <Label className="text-xs">Commission (%)</Label>
                <Input type="number" step="0.1" value={proposedCommission} onChange={(e) => setProposedCommission(e.target.value)}
                  placeholder="Ex 17.5" data-testid="modif-commission" />
              </div>
              <div>
                <Label className="text-xs">CAPEX total (€)</Label>
                <Input type="number" step="1000" value={proposedCapex} onChange={(e) => setProposedCapex(e.target.value)}
                  placeholder="Optionnel" data-testid="modif-capex" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Autres conditions</Label>
              <textarea value={proposedOther} onChange={(e) => setProposedOther(e.target.value)}
                placeholder="Durée, conditions de sortie, prérequis spécifiques…"
                className="w-full border rounded-md p-2 text-xs min-h-[60px] mt-1"
                data-testid="modif-other" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting || reason.trim().length < 5}
            className="bg-red-600 hover:bg-red-700 text-white gap-1"
            data-testid="submit-modif-request">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer à la Direction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Re-export icons used in banners for consumers (not strictly needed)
export { CheckCircle2, XCircle };
